import { Injectable } from "@angular/core";
import { Observable, from, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { environment } from "../../../environments/environment";
import { SessionService } from "../../core/storage/session.service";
import { BannerType } from "./banner-type.enum";
import { Banner } from "./banner.model";

interface IBannerConfig {
  enabled?: boolean;
  id?: string;
  message?: string;
  type?: string;
}

@Injectable({
  providedIn: "root",
})
export class BannerService {
  private static readonly dismissedKey = "dismissed_banners";
  private static readonly defaultPollIntervalMs = 60000;

  // How often the banner auto-refreshes on its own (independent of navigation),
  // so an enabled/updated/disabled banner surfaces without the user clicking
  // around. Overridable per environment via `environment.bannerPollIntervalMs`.
  readonly pollIntervalMs =
    (environment as any).bannerPollIntervalMs || BannerService.defaultPollIntervalMs;

  // Public S3 JSON that controls the banner without a software release.
  // Can be overridden per environment via `environment.bannerConfigUrl`.
  private readonly bannerConfigUrl =
    (environment as any).bannerConfigUrl ||
    "https://hdvi-public.s3.us-east-1.amazonaws.com/dev/banner/banner.json";

  constructor(private readonly sessionService: SessionService) { }

  getActiveBanners(): Observable<Banner[]> {
    return from(this.fetchBannerConfig()).pipe(
      map(config => this.toBanners(config)),
      // Missing file, network/CORS error, or bad JSON -> show no banner.
      catchError(() => of([] as Banner[]))
    );
  }

  dismiss(id: string): void {
    if (!id) {
      return;
    }

    const dismissed = this.getDismissedIds();
    if (!dismissed.includes(id)) {
      dismissed.push(id);
      this.sessionService.put(BannerService.dismissedKey, JSON.stringify(dismissed));
    }
  }

  isDismissed(id: string): boolean {
    return this.getDismissedIds().includes(id);
  }

  // Fetched with the native Fetch API on purpose: it bypasses the app's HTTP
  // interceptors, which would otherwise attach internal auth headers to this
  // third-party (S3) request and break CORS / leak user data.
  private async fetchBannerConfig(): Promise<IBannerConfig | null> {
    const response = await fetch(this.bannerConfigUrl, { cache: "no-store" });
    if (!response.ok) {
      return null;
    }
    return response.json();
  }

  private toBanners(config: IBannerConfig | null): Banner[] {
    if (!config || config.enabled !== true || !config.message) {
      return [];
    }

    return [
      new Banner({
        id: config.id,
        type: this.toBannerType(config.type),
        message: config.message,
        isDismissible: true,
      }),
    ];
  }

  private toBannerType(type?: string): BannerType {
    const match = Object.values(BannerType).find(
      value => value.toLowerCase() === (type || "").toLowerCase()
    );
    return (match as BannerType) || BannerType.Warning;
  }

  private getDismissedIds(): string[] {
    const raw = this.sessionService.get<string>(BannerService.dismissedKey, "[]");
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
}
